import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Download, Search } from 'lucide-react'
import { REFERRALS_PAGE_SIZE } from '@/features/referrals/api.ts'
import { ReferralsTable } from '@/features/referrals/components/referrals-table.tsx'
import {
  readReferralFilters,
  referralFiltersToParams,
  referralOwnerPath,
  type ReferralFilters,
} from '@/features/referrals/filters.ts'
import { OWNER_TYPE_TABS } from '@/features/referrals/labels.ts'
import { useExportReferrals, useReferrals } from '@/features/referrals/use-referrals.ts'
import { ApiError } from '@/shared/api/client.ts'
import { cn } from '@/shared/lib/cn.ts'
import { Button } from '@/shared/ui/button.tsx'
import { Card } from '@/shared/ui/card.tsx'
import { Chip } from '@/shared/ui/chip.tsx'
import { Alert, EmptyState, ErrorState, Skeleton } from '@/shared/ui/states.tsx'

const SEARCH_DEBOUNCE_MS = 300

export function ReferralsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const filters = useMemo(() => readReferralFilters(searchParams), [searchParams])
  const { search: listSearch } = useLocation()
  const navigate = useNavigate()

  const [search, setSearch] = useState(filters.search)
  const [appliedSearch, setAppliedSearch] = useState(filters.search)
  if (filters.search !== appliedSearch) {
    setAppliedSearch(filters.search)
    if (filters.search !== search.trim()) setSearch(filters.search)
  }

  const list = useReferrals(filters)
  const exportCsv = useExportReferrals()

  const applyFilters = (patch: Partial<ReferralFilters>) =>
    setSearchParams(referralFiltersToParams({ ...filters, page: 1, ...patch }))

  const goToPage = (page: number) => {
    applyFilters({ page })
    window.scrollTo({ top: 0 })
  }

  useEffect(() => {
    const query = search.trim()
    if (query === filters.search) return

    const timer = setTimeout(
      () => setSearchParams(referralFiltersToParams({ ...filters, search: query, page: 1 }), { replace: true }),
      SEARCH_DEBOUNCE_MS,
    )
    return () => clearTimeout(timer)
  }, [search, filters, setSearchParams])

  const rows = list.data?.rows ?? []
  const total = list.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / REFERRALS_PAGE_SIZE))
  const firstShown = (filters.page - 1) * REFERRALS_PAGE_SIZE + 1
  const isFiltered = Boolean(filters.search) || filters.suspiciousOnly

  return (
    <>
      <header className="mb-6 flex flex-wrap items-end gap-x-5 gap-y-3.5">
        <div className="min-w-0 flex-1 basis-[300px]">
          <h1 className="mb-2 font-display text-[25px] leading-none font-semibold tracking-[-0.02em] md:text-[32px]">
            Рефералы
          </h1>
          <p className="text-[15px] text-ink-muted text-balance-pretty">
            Кто кого пригласил по промокодам. Активный — приглашённый пользователь сделал первую завершённую запись,
            а бизнес оплатил первый месяц подписки.
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          icon={<Download className="size-3.5" />}
          loading={exportCsv.isPending}
          disabled={total === 0}
          onClick={() => exportCsv.mutate(filters)}
          className="max-md:min-h-11"
        >
          {exportCsv.isPending ? 'Готовим CSV…' : 'Скачать CSV'}
        </Button>
      </header>

      {list.data && !list.data.available ? (
        <Alert>
          Бэкенд Manaro не подключён: в настройках сервера не заданы <code>MANARO_API_URL</code> и{' '}
          <code>MANARO_STATS_TOKEN</code>.
        </Alert>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2 max-md:[&>button]:min-h-11">
            {OWNER_TYPE_TABS.map((tab) => (
              <Chip
                key={tab.value}
                label={tab.label}
                active={filters.ownerType === tab.value}
                onClick={() => applyFilters({ ownerType: tab.value })}
              />
            ))}
            <Chip
              label="Только подозрительные"
              tone="tint"
              active={filters.suspiciousOnly}
              onClick={() => applyFilters({ suspiciousOnly: !filters.suspiciousOnly })}
            />
          </div>

          {exportCsv.error && (
            <Alert>
              {exportCsv.error instanceof ApiError ? exportCsv.error.message : 'Не удалось выгрузить CSV.'}
            </Alert>
          )}

          <Card className="overflow-hidden">
            <label className="flex items-center gap-2.5 border-b border-line-soft px-4 py-3.5 md:px-5">
              <Search className="size-4 shrink-0 text-ink-faint" aria-hidden />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Поиск по коду, имени или почте владельца…"
                className="w-full bg-transparent text-[16px] outline-none placeholder:text-ink-faint md:text-[14.5px]"
              />
            </label>

            {list.isPending ? (
              <div className="flex flex-col gap-3 p-5">
                <Skeleton className="h-10" />
                <Skeleton className="h-10" />
                <Skeleton className="h-10" />
              </div>
            ) : list.error ? (
              <ErrorState
                message={list.error instanceof ApiError ? list.error.message : 'Не удалось загрузить рефералов.'}
                onRetry={() => void list.refetch()}
              />
            ) : rows.length === 0 ? (
              <EmptyState
                title={isFiltered ? 'Никого не нашли' : 'Кодов пока нет'}
                description={
                  isFiltered
                    ? 'Попробуйте другой запрос или снимите фильтр «Только подозрительные».'
                    : 'Коды появятся, как только владельцы заведут аккаунты.'
                }
              />
            ) : (
              <div className={cn('transition-opacity', list.isPlaceholderData && 'opacity-60')}>
                <ReferralsTable
                  rows={rows}
                  ownerType={filters.ownerType}
                  sort={filters.sort}
                  listSearch={listSearch}
                  onSort={(sort) => applyFilters({ sort })}
                  onOpen={(code) => navigate(referralOwnerPath(code), { state: { back: listSearch } })}
                />
              </div>
            )}

            {list.data && total > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line-soft px-4 py-3 md:px-5">
                <span className="tabular text-[12.5px] text-ink-muted">
                  {rows.length > 0 ? `${firstShown}–${firstShown + rows.length - 1} из ${total}` : `Всего ${total}`}
                </span>
                <div className="flex items-center gap-2 max-md:w-full max-md:[&>button]:min-h-11 max-md:[&>button]:flex-1">
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<ChevronLeft className="size-3.5" />}
                    disabled={filters.page <= 1}
                    onClick={() => goToPage(filters.page - 1)}
                    className="disabled:opacity-50"
                  >
                    Назад
                  </Button>
                  <span className="tabular text-[12.5px] text-ink-muted">
                    {filters.page} / {pageCount}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={filters.page >= pageCount}
                    onClick={() => goToPage(filters.page + 1)}
                    className="disabled:opacity-50"
                  >
                    Дальше
                    <ChevronRight className="size-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}
    </>
  )
}
